/**
 * Generated by orval 🍺
 * Do not edit manually.
 * CommunityCliQ API
 *  ## Authentication
 *
 *   Every request must include an API access token generated in the CommunityCliQ \
 *   application:
 *
 *   ```
 *   Authorization: X-API-TOKEN
 *   ```
 *
 * OpenAPI spec version: V2.X
 */
import { useMutation, useQuery } from "@tanstack/react-query";
import type {
  DataTag,
  DefinedInitialDataOptions,
  DefinedUseQueryResult,
  MutationFunction,
  QueryClient,
  QueryFunction,
  QueryKey,
  UndefinedInitialDataOptions,
  UseMutationOptions,
  UseMutationResult,
  UseQueryOptions,
  UseQueryResult,
} from "@tanstack/react-query";

import type {
  BrandingColorResponse,
  BrandingColorsRequest,
  CommandErrorResponse,
  GlobalBrandingResponse,
  HomeBrandingRequest,
  HomeBrandingResponse,
  LandingPageBrandingResponse,
  LogoBrandingRequest,
  LogoBrandingResponse,
  UpdateGlobalBrandingBody,
} from "../../model";

import { customInstance } from "../../../lib/SpringAxios";
import type { ErrorType } from "../../../lib/SpringAxios";
import { formDataGenerator } from "../../../lib/OrvalFormDataGenerator";

type SecondParameter<T extends (...args: never) => unknown> = Parameters<T>[1];

const withQueryKey = <T extends object, K>(
  query: T,
  queryKey: K,
): T & { queryKey: K } => {
  const result = { queryKey } as T & { queryKey: K };
  for (const key of Object.keys(query)) {
    // The explicit queryKey always wins, matching the previous
    // `{ ...query, queryKey }` spread where it was set last.
    if (key === "queryKey") {
      continue;
    }
    Object.defineProperty(result, key, {
      configurable: true,
      enumerable: true,
      get: () => (query as Record<string, unknown>)[key],
    });
  }
  return result;
};

export const getLogoBranding = (
  options?: SecondParameter<typeof customInstance>,
  signal?: AbortSignal,
) => {
  return customInstance<LogoBrandingResponse>(
    { method: "GET", signal, url: `/api/v2/platform/logos` },
    options,
  );
};

export const getGetLogoBrandingQueryKey = () => {
  return [`/api/v2/platform/logos`] as const;
};

export const getGetLogoBrandingQueryOptions = <
  TData = Awaited<ReturnType<typeof getLogoBranding>>,
  TError = ErrorType<CommandErrorResponse>,
>(options?: {
  query?: Partial<
    UseQueryOptions<Awaited<ReturnType<typeof getLogoBranding>>, TError, TData>
  >;
  request?: SecondParameter<typeof customInstance>;
}) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetLogoBrandingQueryKey();

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getLogoBranding>>> = ({
    signal,
  }) => getLogoBranding(requestOptions, signal);

  return { queryFn, queryKey, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getLogoBranding>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetLogoBrandingQueryResult = NonNullable<
  Awaited<ReturnType<typeof getLogoBranding>>
>;
export type GetLogoBrandingQueryError = ErrorType<CommandErrorResponse>;

export function useGetLogoBranding<
  TData = Awaited<ReturnType<typeof getLogoBranding>>,
  TError = ErrorType<CommandErrorResponse>,
>(
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getLogoBranding>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getLogoBranding>>,
          TError,
          Awaited<ReturnType<typeof getLogoBranding>>
        >,
        "initialData"
      >;
    request?: SecondParameter<typeof customInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetLogoBranding<
  TData = Awaited<ReturnType<typeof getLogoBranding>>,
  TError = ErrorType<CommandErrorResponse>,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getLogoBranding>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getLogoBranding>>,
          TError,
          Awaited<ReturnType<typeof getLogoBranding>>
        >,
        "initialData"
      >;
    request?: SecondParameter<typeof customInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetLogoBranding<
  TData = Awaited<ReturnType<typeof getLogoBranding>>,
  TError = ErrorType<CommandErrorResponse>,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getLogoBranding>>,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof customInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};

export function useGetLogoBranding<
  TData = Awaited<ReturnType<typeof getLogoBranding>>,
  TError = ErrorType<CommandErrorResponse>,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getLogoBranding>>,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof customInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
} {
  const queryOptions = getGetLogoBrandingQueryOptions(options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * Update the branding logos.
 * @summary Update the branding logos.
 */
export const updateBrandingLogos = (
  logoBrandingRequest: LogoBrandingRequest,
  options?: SecondParameter<typeof customInstance>,
  signal?: AbortSignal,
) => {
  return customInstance<void>(
    {
      data: logoBrandingRequest,
      headers: { "Content-Type": "application/json" },
      method: "PUT",
      signal,
      url: `/api/v2/platform/logos`,
    },
    options,
  );
};

export const getUpdateBrandingLogosMutationKey = () =>
  ["updateBrandingLogos"] as const;

export const getUpdateBrandingLogosMutationOptions = <
  TError = ErrorType<CommandErrorResponse>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof updateBrandingLogos>>,
    TError,
    UpdateBrandingLogosMutationVariables,
    TContext
  >;
  request?: SecondParameter<typeof customInstance>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof updateBrandingLogos>>,
  TError,
  UpdateBrandingLogosMutationVariables,
  TContext
> => {
  const mutationKey = getUpdateBrandingLogosMutationKey();
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof updateBrandingLogos>>,
    UpdateBrandingLogosMutationVariables
  > = (props) => {
    const { data } = props ?? {};

    return updateBrandingLogos(data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type UpdateBrandingLogosMutationResult = NonNullable<
  Awaited<ReturnType<typeof updateBrandingLogos>>
>;
export type UpdateBrandingLogosMutationBody = LogoBrandingRequest;
export type UpdateBrandingLogosMutationError = ErrorType<CommandErrorResponse>;
export type UpdateBrandingLogosMutationVariables = {
  data: LogoBrandingRequest;
};

/**
 * @summary Update the branding logos.
 */
export const useUpdateBrandingLogos = <
  TError = ErrorType<CommandErrorResponse>,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof updateBrandingLogos>>,
      TError,
      UpdateBrandingLogosMutationVariables,
      TContext
    >;
    request?: SecondParameter<typeof customInstance>;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof updateBrandingLogos>>,
  TError,
  UpdateBrandingLogosMutationVariables,
  TContext
> => {
  return useMutation(
    getUpdateBrandingLogosMutationOptions(options),
    queryClient,
  );
};
export const getHomeBranding = (
  options?: SecondParameter<typeof customInstance>,
  signal?: AbortSignal,
) => {
  return customInstance<HomeBrandingResponse>(
    { method: "GET", signal, url: `/api/v2/platform/home-branding` },
    options,
  );
};

export const getGetHomeBrandingQueryKey = () => {
  return [`/api/v2/platform/home-branding`] as const;
};

export const getGetHomeBrandingQueryOptions = <
  TData = Awaited<ReturnType<typeof getHomeBranding>>,
  TError = ErrorType<CommandErrorResponse>,
>(options?: {
  query?: Partial<
    UseQueryOptions<Awaited<ReturnType<typeof getHomeBranding>>, TError, TData>
  >;
  request?: SecondParameter<typeof customInstance>;
}) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetHomeBrandingQueryKey();

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getHomeBranding>>> = ({
    signal,
  }) => getHomeBranding(requestOptions, signal);

  return { queryFn, queryKey, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getHomeBranding>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetHomeBrandingQueryResult = NonNullable<
  Awaited<ReturnType<typeof getHomeBranding>>
>;
export type GetHomeBrandingQueryError = ErrorType<CommandErrorResponse>;

export function useGetHomeBranding<
  TData = Awaited<ReturnType<typeof getHomeBranding>>,
  TError = ErrorType<CommandErrorResponse>,
>(
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getHomeBranding>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getHomeBranding>>,
          TError,
          Awaited<ReturnType<typeof getHomeBranding>>
        >,
        "initialData"
      >;
    request?: SecondParameter<typeof customInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetHomeBranding<
  TData = Awaited<ReturnType<typeof getHomeBranding>>,
  TError = ErrorType<CommandErrorResponse>,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getHomeBranding>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getHomeBranding>>,
          TError,
          Awaited<ReturnType<typeof getHomeBranding>>
        >,
        "initialData"
      >;
    request?: SecondParameter<typeof customInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetHomeBranding<
  TData = Awaited<ReturnType<typeof getHomeBranding>>,
  TError = ErrorType<CommandErrorResponse>,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getHomeBranding>>,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof customInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};

export function useGetHomeBranding<
  TData = Awaited<ReturnType<typeof getHomeBranding>>,
  TError = ErrorType<CommandErrorResponse>,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getHomeBranding>>,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof customInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
} {
  const queryOptions = getGetHomeBrandingQueryOptions(options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * Update the global home branding of the app.
 * @summary Update the global home branding of the app.
 */
export const updateHomeBranding = (
  homeBrandingRequest: HomeBrandingRequest,
  options?: SecondParameter<typeof customInstance>,
  signal?: AbortSignal,
) => {
  return customInstance<void>(
    {
      data: homeBrandingRequest,
      headers: { "Content-Type": "application/json" },
      method: "PUT",
      signal,
      url: `/api/v2/platform/home-branding`,
    },
    options,
  );
};

export const getUpdateHomeBrandingMutationKey = () =>
  ["updateHomeBranding"] as const;

export const getUpdateHomeBrandingMutationOptions = <
  TError = ErrorType<CommandErrorResponse>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof updateHomeBranding>>,
    TError,
    UpdateHomeBrandingMutationVariables,
    TContext
  >;
  request?: SecondParameter<typeof customInstance>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof updateHomeBranding>>,
  TError,
  UpdateHomeBrandingMutationVariables,
  TContext
> => {
  const mutationKey = getUpdateHomeBrandingMutationKey();
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof updateHomeBranding>>,
    UpdateHomeBrandingMutationVariables
  > = (props) => {
    const { data } = props ?? {};

    return updateHomeBranding(data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type UpdateHomeBrandingMutationResult = NonNullable<
  Awaited<ReturnType<typeof updateHomeBranding>>
>;
export type UpdateHomeBrandingMutationBody = HomeBrandingRequest;
export type UpdateHomeBrandingMutationError = ErrorType<CommandErrorResponse>;
export type UpdateHomeBrandingMutationVariables = { data: HomeBrandingRequest };

/**
 * @summary Update the global home branding of the app.
 */
export const useUpdateHomeBranding = <
  TError = ErrorType<CommandErrorResponse>,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof updateHomeBranding>>,
      TError,
      UpdateHomeBrandingMutationVariables,
      TContext
    >;
    request?: SecondParameter<typeof customInstance>;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof updateHomeBranding>>,
  TError,
  UpdateHomeBrandingMutationVariables,
  TContext
> => {
  return useMutation(
    getUpdateHomeBrandingMutationOptions(options),
    queryClient,
  );
};
/**
 * @deprecated
 */
export const getGlobalBranding = (
  options?: SecondParameter<typeof customInstance>,
  signal?: AbortSignal,
) => {
  return customInstance<GlobalBrandingResponse>(
    { method: "GET", signal, url: `/api/v2/platform/global-branding` },
    options,
  );
};

export const getGetGlobalBrandingQueryKey = () => {
  return [`/api/v2/platform/global-branding`] as const;
};

export const getGetGlobalBrandingQueryOptions = <
  TData = Awaited<ReturnType<typeof getGlobalBranding>>,
  TError = ErrorType<CommandErrorResponse>,
>(options?: {
  query?: Partial<
    UseQueryOptions<
      Awaited<ReturnType<typeof getGlobalBranding>>,
      TError,
      TData
    >
  >;
  request?: SecondParameter<typeof customInstance>;
}) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetGlobalBrandingQueryKey();

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getGlobalBranding>>
  > = ({ signal }) => getGlobalBranding(requestOptions, signal);

  return { queryFn, queryKey, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getGlobalBranding>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetGlobalBrandingQueryResult = NonNullable<
  Awaited<ReturnType<typeof getGlobalBranding>>
>;
export type GetGlobalBrandingQueryError = ErrorType<CommandErrorResponse>;

export function useGetGlobalBranding<
  TData = Awaited<ReturnType<typeof getGlobalBranding>>,
  TError = ErrorType<CommandErrorResponse>,
>(
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getGlobalBranding>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getGlobalBranding>>,
          TError,
          Awaited<ReturnType<typeof getGlobalBranding>>
        >,
        "initialData"
      >;
    request?: SecondParameter<typeof customInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetGlobalBranding<
  TData = Awaited<ReturnType<typeof getGlobalBranding>>,
  TError = ErrorType<CommandErrorResponse>,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getGlobalBranding>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getGlobalBranding>>,
          TError,
          Awaited<ReturnType<typeof getGlobalBranding>>
        >,
        "initialData"
      >;
    request?: SecondParameter<typeof customInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetGlobalBranding<
  TData = Awaited<ReturnType<typeof getGlobalBranding>>,
  TError = ErrorType<CommandErrorResponse>,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getGlobalBranding>>,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof customInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
/**
 * @deprecated
 */

export function useGetGlobalBranding<
  TData = Awaited<ReturnType<typeof getGlobalBranding>>,
  TError = ErrorType<CommandErrorResponse>,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getGlobalBranding>>,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof customInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
} {
  const queryOptions = getGetGlobalBrandingQueryOptions(options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * Update the global branding.
 * @deprecated
 * @summary Update the global branding.
 */
export const updateGlobalBranding = (
  updateGlobalBrandingBody?: UpdateGlobalBrandingBody,
  options?: SecondParameter<typeof customInstance>,
  signal?: AbortSignal,
) => {
  const formData = formDataGenerator(updateGlobalBrandingBody);
  return customInstance<void>(
    {
      data: formData,
      headers: { "Content-Type": "multipart/form-data" },
      method: "PUT",
      signal,
      url: `/api/v2/platform/global-branding`,
    },
    options,
  );
};

export const getUpdateGlobalBrandingMutationKey = () =>
  ["updateGlobalBranding"] as const;

export const getUpdateGlobalBrandingMutationOptions = <
  TError = ErrorType<CommandErrorResponse>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof updateGlobalBranding>>,
    TError,
    UpdateGlobalBrandingMutationVariables,
    TContext
  >;
  request?: SecondParameter<typeof customInstance>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof updateGlobalBranding>>,
  TError,
  UpdateGlobalBrandingMutationVariables,
  TContext
> => {
  const mutationKey = getUpdateGlobalBrandingMutationKey();
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof updateGlobalBranding>>,
    UpdateGlobalBrandingMutationVariables
  > = (props) => {
    const { data } = props ?? {};

    return updateGlobalBranding(data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type UpdateGlobalBrandingMutationResult = NonNullable<
  Awaited<ReturnType<typeof updateGlobalBranding>>
>;
export type UpdateGlobalBrandingMutationBody =
  UpdateGlobalBrandingBody | undefined;
export type UpdateGlobalBrandingMutationError = ErrorType<CommandErrorResponse>;
export type UpdateGlobalBrandingMutationVariables = {
  data?: UpdateGlobalBrandingBody;
};

/**
 * @deprecated
 * @summary Update the global branding.
 */
export const useUpdateGlobalBranding = <
  TError = ErrorType<CommandErrorResponse>,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof updateGlobalBranding>>,
      TError,
      UpdateGlobalBrandingMutationVariables,
      TContext
    >;
    request?: SecondParameter<typeof customInstance>;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof updateGlobalBranding>>,
  TError,
  UpdateGlobalBrandingMutationVariables,
  TContext
> => {
  return useMutation(
    getUpdateGlobalBrandingMutationOptions(options),
    queryClient,
  );
};
export const getBrandingColors = (
  options?: SecondParameter<typeof customInstance>,
  signal?: AbortSignal,
) => {
  return customInstance<BrandingColorResponse[]>(
    { method: "GET", signal, url: `/api/v2/platform/colors` },
    options,
  );
};

export const getGetBrandingColorsQueryKey = () => {
  return [`/api/v2/platform/colors`] as const;
};

export const getGetBrandingColorsQueryOptions = <
  TData = Awaited<ReturnType<typeof getBrandingColors>>,
  TError = ErrorType<CommandErrorResponse>,
>(options?: {
  query?: Partial<
    UseQueryOptions<
      Awaited<ReturnType<typeof getBrandingColors>>,
      TError,
      TData
    >
  >;
  request?: SecondParameter<typeof customInstance>;
}) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetBrandingColorsQueryKey();

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getBrandingColors>>
  > = ({ signal }) => getBrandingColors(requestOptions, signal);

  return { queryFn, queryKey, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getBrandingColors>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetBrandingColorsQueryResult = NonNullable<
  Awaited<ReturnType<typeof getBrandingColors>>
>;
export type GetBrandingColorsQueryError = ErrorType<CommandErrorResponse>;

export function useGetBrandingColors<
  TData = Awaited<ReturnType<typeof getBrandingColors>>,
  TError = ErrorType<CommandErrorResponse>,
>(
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getBrandingColors>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getBrandingColors>>,
          TError,
          Awaited<ReturnType<typeof getBrandingColors>>
        >,
        "initialData"
      >;
    request?: SecondParameter<typeof customInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetBrandingColors<
  TData = Awaited<ReturnType<typeof getBrandingColors>>,
  TError = ErrorType<CommandErrorResponse>,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getBrandingColors>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getBrandingColors>>,
          TError,
          Awaited<ReturnType<typeof getBrandingColors>>
        >,
        "initialData"
      >;
    request?: SecondParameter<typeof customInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetBrandingColors<
  TData = Awaited<ReturnType<typeof getBrandingColors>>,
  TError = ErrorType<CommandErrorResponse>,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getBrandingColors>>,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof customInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};

export function useGetBrandingColors<
  TData = Awaited<ReturnType<typeof getBrandingColors>>,
  TError = ErrorType<CommandErrorResponse>,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getBrandingColors>>,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof customInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
} {
  const queryOptions = getGetBrandingColorsQueryOptions(options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * Update branding colors.
 * @summary Update branding colors.
 */
export const updateBrandingColors = (
  brandingColorsRequest: BrandingColorsRequest,
  options?: SecondParameter<typeof customInstance>,
  signal?: AbortSignal,
) => {
  return customInstance<void>(
    {
      data: brandingColorsRequest,
      headers: { "Content-Type": "application/json" },
      method: "PUT",
      signal,
      url: `/api/v2/platform/colors`,
    },
    options,
  );
};

export const getUpdateBrandingColorsMutationKey = () =>
  ["updateBrandingColors"] as const;

export const getUpdateBrandingColorsMutationOptions = <
  TError = ErrorType<CommandErrorResponse>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof updateBrandingColors>>,
    TError,
    UpdateBrandingColorsMutationVariables,
    TContext
  >;
  request?: SecondParameter<typeof customInstance>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof updateBrandingColors>>,
  TError,
  UpdateBrandingColorsMutationVariables,
  TContext
> => {
  const mutationKey = getUpdateBrandingColorsMutationKey();
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof updateBrandingColors>>,
    UpdateBrandingColorsMutationVariables
  > = (props) => {
    const { data } = props ?? {};

    return updateBrandingColors(data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type UpdateBrandingColorsMutationResult = NonNullable<
  Awaited<ReturnType<typeof updateBrandingColors>>
>;
export type UpdateBrandingColorsMutationBody = BrandingColorsRequest;
export type UpdateBrandingColorsMutationError = ErrorType<CommandErrorResponse>;
export type UpdateBrandingColorsMutationVariables = {
  data: BrandingColorsRequest;
};

/**
 * @summary Update branding colors.
 */
export const useUpdateBrandingColors = <
  TError = ErrorType<CommandErrorResponse>,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof updateBrandingColors>>,
      TError,
      UpdateBrandingColorsMutationVariables,
      TContext
    >;
    request?: SecondParameter<typeof customInstance>;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof updateBrandingColors>>,
  TError,
  UpdateBrandingColorsMutationVariables,
  TContext
> => {
  return useMutation(
    getUpdateBrandingColorsMutationOptions(options),
    queryClient,
  );
};
/**
 * @deprecated
 */
export const getLandingPageBranding = (
  options?: SecondParameter<typeof customInstance>,
  signal?: AbortSignal,
) => {
  return customInstance<LandingPageBrandingResponse>(
    { method: "GET", signal, url: `/api/v2/platform/landing-page-branding` },
    options,
  );
};

export const getGetLandingPageBrandingQueryKey = () => {
  return [`/api/v2/platform/landing-page-branding`] as const;
};

export const getGetLandingPageBrandingQueryOptions = <
  TData = Awaited<ReturnType<typeof getLandingPageBranding>>,
  TError = ErrorType<CommandErrorResponse>,
>(options?: {
  query?: Partial<
    UseQueryOptions<
      Awaited<ReturnType<typeof getLandingPageBranding>>,
      TError,
      TData
    >
  >;
  request?: SecondParameter<typeof customInstance>;
}) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getGetLandingPageBrandingQueryKey();

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getLandingPageBranding>>
  > = ({ signal }) => getLandingPageBranding(requestOptions, signal);

  return { queryFn, queryKey, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getLandingPageBranding>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetLandingPageBrandingQueryResult = NonNullable<
  Awaited<ReturnType<typeof getLandingPageBranding>>
>;
export type GetLandingPageBrandingQueryError = ErrorType<CommandErrorResponse>;

export function useGetLandingPageBranding<
  TData = Awaited<ReturnType<typeof getLandingPageBranding>>,
  TError = ErrorType<CommandErrorResponse>,
>(
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getLandingPageBranding>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getLandingPageBranding>>,
          TError,
          Awaited<ReturnType<typeof getLandingPageBranding>>
        >,
        "initialData"
      >;
    request?: SecondParameter<typeof customInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetLandingPageBranding<
  TData = Awaited<ReturnType<typeof getLandingPageBranding>>,
  TError = ErrorType<CommandErrorResponse>,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getLandingPageBranding>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getLandingPageBranding>>,
          TError,
          Awaited<ReturnType<typeof getLandingPageBranding>>
        >,
        "initialData"
      >;
    request?: SecondParameter<typeof customInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetLandingPageBranding<
  TData = Awaited<ReturnType<typeof getLandingPageBranding>>,
  TError = ErrorType<CommandErrorResponse>,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getLandingPageBranding>>,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof customInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
/**
 * @deprecated
 */

export function useGetLandingPageBranding<
  TData = Awaited<ReturnType<typeof getLandingPageBranding>>,
  TError = ErrorType<CommandErrorResponse>,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getLandingPageBranding>>,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof customInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
} {
  const queryOptions = getGetLandingPageBrandingQueryOptions(options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}
